<template>
  <div class="mp" :class="[`mp-${kind}`, { 'mp-compact': compact }]">
    <div v-if="!mediaId" class="mp-missing">
      <i class="fa fa-exclamation-triangle"></i>
      {{ label }}{{ missingText }}
    </div>
    <div v-else-if="state === 'loading'" class="mp-placeholder">
      <i class="fa fa-spinner fa-spin"></i><span v-if="!compact"> Loading {{ label.toLowerCase() }}…</span>
    </div>
    <div v-else-if="state === 'error'" class="mp-missing">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </div>
    <template v-else>
      <img v-if="kind === 'image'" :src="url" :alt="mediaName || 'Image'" class="mp-img" />
      <!-- Players and links stop the click, so using them inside a selectable card doesn't
           also select the card; a click on the picture itself still does. -->
      <video v-else-if="kind === 'video'" :src="url" controls preload="metadata" class="mp-video" @click.stop></video>
      <audio v-else-if="kind === 'audio'" :src="url" controls preload="metadata" class="mp-audio" @click.stop></audio>
      <div v-else class="mp-doc">
        <i class="fa fa-file-pdf-o"></i>
        <span class="mp-doc-name">{{ fileName || mediaName || 'Document' }}</span>
      </div>
    </template>
    <div v-if="mediaId && state !== 'error' && (showCaption || canOpen)" class="mp-caption">
      <span v-if="showCaption && mediaName" class="mp-caption-name">{{ label }} · {{ mediaName }}</span>
      <a v-if="canOpen" href="#" class="mp-open" @click.prevent.stop="open">
        <i class="fa" :class="opening ? 'fa-spinner fa-spin' : 'fa-external-link'"></i> Open file
      </a>
    </div>
  </div>
</template>

<script>
import api from '../../../utils/api'

// A Media Management file, shown as the recipient would see it. The S3 bucket isn't public,
// so the bytes come through GET /nudge-media-file with the console's own login and are shown
// from a blob URL; "Open file" opens that same copy in a new tab. Without a country and
// language the endpoint returns the item's first stored file.
//
// Images and video load straight away (the preview needs the bytes). A document only shows a
// card, so its file (up to 100 MB) is fetched when "Open file" is clicked, not before.
//
// Used by the Create Campaign chat (SystemMessage.vue), Edit Campaign, campaign analytics,
// Media Management, Nudge Template Manager, the /opportunity form and the conversation editor.
const KINDS = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  AUDIO: 'audio',
  image: 'image',
  video: 'video',
  document: 'document',
  audio: 'audio',
  application: 'document'
}

const kindOf = format => KINDS[format] || KINDS[String(format || '').split('/')[0]] || 'document'

// One fetch per file across every preview on the page (Edit Campaign shows the same header
// in its panel and in each language bubble). Holds Blobs, not URLs — each component makes
// and revokes its own URL. Small, since a blob can be up to 16 MB.
const CACHE_LIMIT = 12
const blobCache = new Map()

function fetchBlob (params) {
  const key = JSON.stringify(params)
  if (blobCache.has(key)) return blobCache.get(key)
  const request = api.buildRequest('nudge-media-file', 'GET', params, null, 'blob').then(({ data, status }) => {
    if (status !== 200) {
      blobCache.delete(key)
      const error = new Error(`HTTP ${status}`)
      error.status = status
      throw error
    }
    return data
  }, err => {
    blobCache.delete(key)
    throw err
  })
  blobCache.set(key, request)
  if (blobCache.size > CACHE_LIMIT) blobCache.delete(blobCache.keys().next().value)
  return request
}

export default {
  name: 'media-preview',
  props: {
    // A Turn header format (IMAGE / VIDEO / DOCUMENT), a media.media_type (image / video /
    // document / audio) or a MIME type (image/png ...). Corrected from the file's own type
    // once it loads, so a mismatched label can't render a video as a picture.
    format: { type: String, required: true },
    mediaId: { type: [Number, String], default: null },
    mediaName: { type: String, default: '' },
    fileName: { type: String, default: '' },
    countryId: { type: [Number, String], default: null },
    languageId: { type: [Number, String], default: null },
    // Thumbnail size, for cards and table cells.
    compact: { type: Boolean, default: false },
    showCaption: { type: Boolean, default: true },
    // What to say after the label when there's no file at all.
    missingText: { type: String, default: ' header, but no file is set on the template. WhatsApp will reject the send.' }
  },
  data () {
    return { state: 'loading', url: '', error: '', blobKind: '', opening: false }
  },
  computed: {
    kind () {
      return this.blobKind || kindOf(this.format)
    },
    label () {
      return this.kind.charAt(0).toUpperCase() + this.kind.slice(1)
    },
    canOpen () {
      return this.state === 'ready' || this.state === 'lazy'
    },
    params () {
      const params = { mediaId: this.mediaId }
      if (this.countryId) params.countryId = this.countryId
      if (this.languageId) params.languageId = this.languageId
      return params
    },
    key () {
      return `${this.mediaId}|${this.countryId}|${this.languageId}|${this.format}`
    }
  },
  watch: {
    key: { immediate: true, handler () { this.load() } }
  },
  beforeDestroy () {
    this.token = null
    this.release()
  },
  methods: {
    release () {
      if (this.url) URL.revokeObjectURL(this.url)
      this.url = ''
    },
    describeFailure (err) {
      if (err.status === 404) {
        return this.countryId
          ? `"${this.mediaName || 'This media'}" has no file for this country and language in Media Management, so the send would go out without it.`
          : `"${this.mediaName || 'This media'}" has no stored file.`
      }
      return `Couldn't load the file (${err.message}).`
    },
    // Each load gets a token; a response that arrives after a newer load started (or after
    // the component is gone) is dropped instead of overwriting the newer file or leaking.
    async load () {
      this.release()
      this.blobKind = ''
      this.error = ''
      if (!this.mediaId) return
      if (kindOf(this.format) === 'document') {
        this.state = 'lazy'
        return
      }
      const token = {}
      this.token = token
      this.state = 'loading'
      try {
        const blob = await fetchBlob(this.params)
        if (this.token !== token) return
        this.show(blob)
      } catch (err) {
        if (this.token !== token) return
        this.state = 'error'
        this.error = this.describeFailure(err)
      }
    },
    show (blob) {
      this.release()
      this.url = URL.createObjectURL(blob)
      // The file's own type wins over the label it was handed.
      const fromBlob = blob.type ? kindOf(blob.type) : ''
      if (fromBlob && fromBlob !== 'document') this.blobKind = fromBlob
      this.state = 'ready'
    },
    async open () {
      if (this.state === 'lazy') {
        this.opening = true
        const token = {}
        this.token = token
        try {
          const blob = await fetchBlob(this.params)
          if (this.token !== token) return
          this.release()
          this.url = URL.createObjectURL(blob)
          this.state = 'ready'
        } catch (err) {
          if (this.token !== token) return
          this.state = 'error'
          this.error = this.describeFailure(err)
          return
        } finally {
          this.opening = false
        }
      }
      if (!this.url) return
      if (this.kind === 'document') {
        // A download link for documents, named as the person would see it.
        const link = document.createElement('a')
        link.href = this.url
        link.target = '_blank'
        link.rel = 'noopener'
        link.download = this.fileName || this.mediaName || 'document.pdf'
        link.click()
      } else {
        window.open(this.url, '_blank', 'noopener')
      }
    }
  }
}
</script>

<style scoped>
.mp { margin: -2px -2px 6px; }
.mp-img, .mp-video { display: block; width: 100%; max-height: 220px; object-fit: cover; border-radius: 6px; background: #e9edef; }
.mp-audio { width: 100%; }
.mp-placeholder, .mp-missing { padding: 18px 10px; border-radius: 6px; font-size: 12px; text-align: center; background: #e9edef; color: #54656f; }
.mp-missing { background: #fdecea; color: #b42318; text-align: left; }
.mp-doc { display: flex; align-items: center; gap: 8px; padding: 10px; border-radius: 6px; background: #f0f2f5; color: #111b21; font-size: 13px; }
.mp-doc .fa { font-size: 22px; color: #d93025; }
.mp-doc-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mp-caption { display: flex; align-items: center; gap: 8px; margin-top: 4px; font-size: 11px; color: #667781; }
.mp-caption-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mp-open { margin-left: auto; color: #027eb5; white-space: nowrap; }

.mp-compact { margin: 6px 0 0; max-width: 180px; }
.mp-compact .mp-img, .mp-compact .mp-video { max-height: 110px; }
.mp-compact .mp-placeholder, .mp-compact .mp-missing { padding: 10px 8px; font-size: 11px; }
.mp-compact .mp-doc { padding: 6px 8px; font-size: 12px; }
</style>



// WEBPACK FOOTER //
// MediaPreview.vue?88b56964